import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, dao } from '@platform/dao';
import type { IAttachment } from '@platform/core/types';
import type { IFilePreview } from './IFilePreview';
import { previewKindOf } from './previewKind';

/**
 * Перегляд зображення чи PDF у вікні (UIUX field-types/file): вміст береться через DAO з тими самими заголовками авторизації, що й
 * завантаження, і показується з локальної blob-адреси; тип blob задається явно (PDF — application/pdf), тож вміст не виконується як
 * сторінка. Закриття чи новий перегляд звільняє попередню адресу; відповідь застарілого запиту ігнорується.
 */
export function useFilePreview() {
  const { t } = useTranslation();
  const [preview, setPreview] = useState<IFilePreview | null>(null);
  const url = useRef<string | null>(null);
  const seq = useRef(0);
  const release = () => { if (url.current) URL.revokeObjectURL(url.current); url.current = null; };
  useEffect(() => release, []);
  const open = useCallback(async (file: IAttachment) => {
    const kind = previewKindOf(file.fileName, file.mimeType);
    if (!kind) return;
    const n = ++seq.current;
    release();
    setPreview({ file, kind, url: null, error: null });
    try {
      const { blob } = await dao.collaboration.download(file.id, file.fileName);
      if (n !== seq.current) return;
      const type = kind === 'pdf' ? 'application/pdf' : blob.type.startsWith('image/') ? blob.type : file.mimeType;
      url.current = URL.createObjectURL(new Blob([blob], { type }));
      setPreview({ file, kind, url: url.current, error: null });
    } catch (e) {
      if (n === seq.current) setPreview({ file, kind, url: null, error: t('recordPage.files.previewFailed', { name: file.fileName, message: ApiError.fromUnknown(e).message }) });
    }
  }, [t]);
  const close = useCallback(() => { seq.current += 1; release(); setPreview(null); }, []);
  return { preview, open, close };
}
